export type FilterValueType = "boolean" | "multi" | "number" | "string" | "enum";
export type MultiItemType = "string" | "number";

export interface FilterFieldMapping<TValue = unknown> {
  queryKey: `q.${string}` | string;
  type: FilterValueType;
  persisted?: boolean;
  itemType?: MultiItemType;
  parse?: (value: string) => TValue | undefined;
  serialize?: (value: TValue) => string | undefined;
}

export type FilterMappingConfig = Record<string, FilterFieldMapping>;

const MULTI_VALUE_SEPARATOR = "~";
const LEGACY_MULTI_VALUE_SEPARATOR = ",";
const EXCLUDED_FILTER_VALUES = new Set(["no filter", "no filters", "all providers"]);

const isExcludedStringValue = (value: string): boolean => {
  const trimmedValue = value.trim();
  return trimmedValue === "" || EXCLUDED_FILTER_VALUES.has(trimmedValue.toLowerCase());
};

export const normalizeBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === false) {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (["1", "true", "yes"].includes(normalizedValue)) {
    return true;
  }

  if (["0", "false", "no"].includes(normalizedValue)) {
    return false;
  }

  return undefined;
};

export const normalizeNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

export const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return isExcludedStringValue(trimmedValue) ? undefined : trimmedValue;
};

export const normalizeTimeOfDay = (value: unknown): string | undefined => {
  const normalizedValue = normalizeString(value);
  if (!normalizedValue) {
    return undefined;
  }

  if (/^\d{4}$/.test(normalizedValue)) {
    return `${normalizedValue.slice(0, 2)}:${normalizedValue.slice(2, 4)}`;
  }

  if (/^\d{2}:\d{2}$/.test(normalizedValue)) {
    return normalizedValue;
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(normalizedValue)) {
    return normalizedValue.slice(0, 5);
  }

  return undefined;
};

export const serializeTimeOfDay = (value: unknown): string | undefined => {
  const normalizedValue = normalizeTimeOfDay(value);
  if (!normalizedValue) {
    return undefined;
  }

  return normalizedValue.replace(":", "");
};

export const normalizeMultiSelect = <TValue extends string | number = string>(
  value: unknown,
  itemType: MultiItemType = "string"
): TValue[] => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(new RegExp(`[${MULTI_VALUE_SEPARATOR}${LEGACY_MULTI_VALUE_SEPARATOR}]`))
      : value != null
        ? [value]
        : [];

  return rawValues
    .map((item) => {
      if (itemType === "number") {
        return normalizeNumber(item);
      }

      return normalizeString(String(item));
    })
    .filter((item): item is TValue => item !== undefined);
};

const getQueryValue = (query: URLSearchParams, queryKey: string): string | undefined => {
  const prefixedValue = query.get(queryKey);
  if (prefixedValue !== null) {
    return prefixedValue;
  }

  if (queryKey.startsWith("q.")) {
    const bareValue = query.get(queryKey.substring(2));
    return bareValue !== null ? bareValue : undefined;
  }

  return undefined;
};

const parseMappedValue = (rawValue: string, field: FilterFieldMapping): unknown => {
  if (field.parse) {
    return field.parse(rawValue);
  }

  switch (field.type) {
    case "boolean":
      return normalizeBoolean(rawValue);
    case "multi":
      return normalizeMultiSelect(rawValue, field.itemType);
    case "number":
      return normalizeNumber(rawValue);
    case "string": {
      // Preserve internal spaces (e.g. "123 456" where space is a delimiter).
      // Only exclude truly empty or sentinel values (checked on trimmed copy).
      const trimmed = rawValue.trim();
      return isExcludedStringValue(trimmed) ? undefined : rawValue;
    }
    case "enum":
      return normalizeString(rawValue);
    default:
      return undefined;
  }
};

const normalizeParsedValue = (value: unknown, field: FilterFieldMapping): unknown => {
  if (field.type === "multi") {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === undefined || value === null || value === "") {
      return [];
    }

    return normalizeMultiSelect(value, field.itemType);
  }

  return value;
};

const serializeMappedValue = (value: unknown, field: FilterFieldMapping): string | undefined => {
  if (field.serialize) {
    return field.serialize(value);
  }

  switch (field.type) {
    case "boolean": {
      const normalizedValue = normalizeBoolean(value);
      if (normalizedValue === undefined) {
        return undefined;
      }

      return normalizedValue ? "1" : "0";
    }
    case "multi": {
      const normalizedValue = normalizeMultiSelect(value, field.itemType);
      return normalizedValue.length > 0 ? normalizedValue.join(MULTI_VALUE_SEPARATOR) : undefined;
    }
    case "number": {
      const normalizedValue = normalizeNumber(value);
      return normalizedValue !== undefined ? String(normalizedValue) : undefined;
    }
    case "string": {
      // Preserve internal/trailing spaces so that users can type space-delimited
      // ID lists (e.g. "123 456"). Only exclude truly empty or sentinel values.
      if (typeof value !== "string") return undefined;
      const trimmed = value.trim();
      return isExcludedStringValue(trimmed) ? undefined : value;
    }
    case "enum": {
      return normalizeString(value);
    }
    default:
      return undefined;
  }
};

export const parseQueryToFilters = <TFilters extends object>(
  query: URLSearchParams | string,
  mapping: FilterMappingConfig
): Partial<TFilters> => {
  const params = typeof query === "string" ? new URLSearchParams(query) : query;
  const parsedFilters: Partial<TFilters> = {};

  Object.entries(mapping).forEach(([fieldName, fieldMapping]) => {
    const rawValue = getQueryValue(params, fieldMapping.queryKey);

    if (rawValue === undefined) {
      return;
    }

    const parsedValue = normalizeParsedValue(parseMappedValue(rawValue, fieldMapping), fieldMapping);
    if (parsedValue !== undefined) {
      parsedFilters[fieldName as keyof TFilters] = parsedValue as TFilters[keyof TFilters];
    }
  });

  // Debug logging removed — was noisy in dev console

  return parsedFilters;
};

export const buildQueryFromFilters = (
  filters: object,
  mapping: FilterMappingConfig,
  options: { persistedOnly?: boolean } = {}
): string => {
  const params = new URLSearchParams();
  const filterValues = filters as Record<string, unknown>;

  Object.entries(mapping).forEach(([fieldName, fieldMapping]) => {
    if (options.persistedOnly && fieldMapping.persisted === false) {
      return;
    }

    const serializedValue = serializeMappedValue(filterValues[fieldName], fieldMapping);
    if (serializedValue !== undefined) {
      params.set(fieldMapping.queryKey, serializedValue);
    }
  });

  return params.toString().replace(/%7E/gi, MULTI_VALUE_SEPARATOR);
};
